import type { MakeupCredit } from '@court-sync/shared';
import { apiGet, apiPost, apiPatch, apiDelete, apiClient } from './apiClient';

export interface CreditBalance {
  available: number;
  expiring_soon: number;
  used: number;
  expired: number;
  total_earned: number;
}

export interface CreditsByStudent {
  studentId: string;
  studentName: string;
  availableCount: number;
  creditId?: string;
  expiresAt?: string;
  scope?: 'session' | 'term';
}

export interface CreditHistoryEarnedFromSession {
  dateYmd: string;
  startTime?: string;
  endTime?: string;
}

export type CreditHistoryRedeemedSession =
  | {
      kind: 'session';
      dateYmd: string;
      lessonName?: string;
      startTime?: string;
      endTime?: string;
    }
  | {
      kind: 'full_term';
      lessonName?: string;
      label: string;
    };

export interface CreditHistoryItem {
  id: string;
  date: string;
  type: 'absence' | 'cancellation' | 'used' | 'expired';
  lessonName: string;
  skillLevel?: string;
  /** Academy sport label when multi-sport academies scope credits. */
  sportLabel?: string;
  academySportId?: string;
  status: 'available' | 'used' | 'expired';
  expiresAt?: string;
  usedAt?: string;
  earnedFromSession?: CreditHistoryEarnedFromSession;
  redeemedSession?: CreditHistoryRedeemedSession;
}

export interface AvailableLessonForCredit {
  lessonId: string;
  lessonName: string;
  skillLevel?: string;
  sportLabel?: string;
  academySportId?: string;
  date: string;
  time: string;
  spotsAvailable: number;
  creditId?: string;
}

export interface AvailableTermLessonForCredit {
  lessonId: string;
  lessonName: string;
  termName: string;
  termDateRange: string;
  skillLevel?: string;
  spotsAvailable: number;
  creditId: string;
}

export const makeupcreditService = {
  async getCreditsForStudent(academyId: string, studentId: string): Promise<MakeupCredit[]> {
    const response = await apiGet<MakeupCredit[]>('/makeup-credits', { params: { academyId, studentId } });
    if (response?.success && Array.isArray(response.data)) {
      return response.data;
    }
    return [];
  },

  async getCreditsOverview(academyId?: string): Promise<{
    balance: CreditBalance;
    history: CreditHistoryItem[];
    byStudent?: CreditsByStudent[];
  }> {
    const params = academyId ? { academyId } : {};
    const { data } = await apiClient.get<{
      balance: CreditBalance;
      history: CreditHistoryItem[];
      byStudent?: CreditsByStudent[];
    }>('/makeup-credits', { params });
    return data;
  },

  async getAvailableLessonsForCredit(academyId?: string, creditId?: string): Promise<AvailableLessonForCredit[]> {
    const params: Record<string, string> = {};
    if (academyId) params.academyId = academyId;
    if (creditId) params.creditId = creditId;
    const response = await apiGet<AvailableLessonForCredit[]>('/makeup-credits/available-lessons', { params });
    return response.data || [];
  },

  async reportAbsence(
    bookingId: string,
    reason?: string,
    options?: { lessonDate?: string; studentId?: string; sessionId?: string }
  ): Promise<{
    creditId: string;
    message: string;
    expiresAt: string;
  }> {
    const { data } = await apiClient.post<{
      creditId: string;
      message: string;
      expiresAt: string;
    }>('/makeup-credits/report-absence', {
      bookingId,
      reason,
      ...(options?.lessonDate && { lessonDate: options.lessonDate }),
      ...(options?.studentId && { studentId: options.studentId }),
      ...(options?.sessionId && { sessionId: options.sessionId }),
    });
    return data;
  },

  async useCredit(
    creditId: string,
    lessonId: string,
    sessionDateOrSessionId: string
  ): Promise<{
    bookingId: string;
    message: string;
  }> {
    const isUuid =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        sessionDateOrSessionId.trim()
      );
    const response = await apiPost<{ bookingId?: string }>(`/makeup-credits/${creditId}/use`, {
      lessonId,
      ...(isUuid ? { sessionId: sessionDateOrSessionId.trim() } : { sessionDate: sessionDateOrSessionId }),
    });
    const payload = response?.data ?? response;
    return {
      bookingId: (payload as { bookingId?: string })?.bookingId ?? '',
      message: (response as { message?: string })?.message ?? '',
    };
  },

  async useTermCredit(creditId: string, lessonId: string): Promise<{
    bookingId: string;
    message: string;
  }> {
    const { data } = await apiClient.post<{ bookingId: string; message: string }>(
      `/makeup-credits/${creditId}/use`,
      { lessonId }
    );
    return data;
  },

  async getTermCreditForLesson(lessonId: string): Promise<string | null> {
    const response = await apiGet<{ creditId?: string }>('/makeup-credits/term-credit-for-lesson', {
      params: { lessonId },
    });
    return response?.data?.creditId ?? null;
  },

  async getAvailableTermLessonsForCredit(
    creditId: string,
    academyId?: string
  ): Promise<AvailableTermLessonForCredit[]> {
    const params: Record<string, string> = { creditId };
    if (academyId) params.academyId = academyId;
    const response = await apiGet<AvailableTermLessonForCredit[]>('/makeup-credits/available-term-lessons', { params });
    return response.data || [];
  },

  async getCreditDetails(creditId: string): Promise<CreditHistoryItem> {
    const response = await apiGet<CreditHistoryItem>(`/makeup-credits/${creditId}`);
    return response.data!;
  },

  async getAvailableCount(): Promise<number> {
    const { data } = await apiClient.get<{ count: number }>('/makeup-credits/available/count');
    return data.count;
  },

  async getPastEligibleLessons(academyId?: string): Promise<
    Array<{
      bookingId: string;
      lessonId: string;
      lessonName: string;
      lessonType: string;
      skillLevel?: string;
      date: string;
      notificationDeadlineHours: number;
      canReportAbsence: boolean;
    }>
  > {
    const params = academyId ? { academyId } : {};
    const response = await apiGet<Array<{
      bookingId: string;
      lessonId: string;
      lessonName: string;
      lessonType: string;
      skillLevel?: string;
      date: string;
      notificationDeadlineHours: number;
      canReportAbsence: boolean;
    }>>('/makeup-credits/eligible-for-absence', { params });
    return response.data || [];
  },

  async manualGrant(params: {
    academyId: string;
    studentId: string;
    lessonId?: string;
    scope?: 'session' | 'term';
    termId?: string;
    expiresAt?: string;
    reason?: string;
  }): Promise<{ id: string }> {
    const response = await apiPost<{ id: string }>('/makeup-credits/manual-grant', params);
    if (response?.success && response?.data?.id) {
      return { id: response.data.id };
    }
    throw new Error(response?.error || 'Failed to grant credit');
  },

  async updateManualCredit(
    creditId: string,
    params: {
      academyId: string;
      scope?: 'session' | 'term';
      termId?: string | null;
      expiresAt?: string;
      reason?: string | null;
      lessonId?: string | null;
    }
  ): Promise<void> {
    const response = await apiPatch(`/makeup-credits/${creditId}`, params);
    if (!response?.success) {
      throw new Error(response?.error || 'Failed to update credit');
    }
  },

  async deleteManualCredit(creditId: string, academyId: string): Promise<void> {
    const response = await apiDelete(`/makeup-credits/${creditId}`, {
      params: { academyId },
    });
    if (!response?.success) {
      throw new Error(response?.error || 'Failed to delete credit');
    }
  },
};

export default makeupcreditService;
